import { useFirestoreID } from "../../firebase/useFirestore";
import { useState, useContext, useEffect, useMemo, useRef } from "react";
import SelectableGrid from "../../components/common/SelectableGrid";
import { db, timestamp } from "../../firebase/config.js"
import StarToggle from "../../components/common/StarToggle.jsx";
import { isStarred, starredPatch } from "../../helpers/starred/starred.js";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { MeasurementSignalsProvider } from '../../StateManagment/MeasurementSignalsContext';
import { SavedIcon } from "../../StateManagment/SavedIcon";
import Location from "../../hooks/Location"
import { useLocation } from "react-router-dom"
import useScrollToHash from "../../hooks/useScrollToHash"
import useLogRecordOpen from "../../hooks/useLogRecordOpen"
import firebase from '../../firebase/compat'
import { bucket } from '../../firebase/config';
import useSettings from "../../hooks/Settings";
import editIcon from '../../images/icons/edit-icon.png'
import { Auth } from "../../StateManagment/Auth";
import usePersonaStatusChange from "../../hooks/personas/usePersonaStatusChange";
import timestampToUI from '../../utils/timestampToUI';
import Data from "./Data";
import Research from "./Research";
import Reports from "./Reports";
import PersonaDashboard from "../../components/personas/Dashboard/PersonaDashboard";
import Shedule from "./Shedule";
import { PersonaNavigation } from "../../StateManagment/PersonaNavigation";
import Goals from "./Goals.jsx";
import Participatieladder from "./Participatieladder.jsx";
import Arbeidsvaardigheden from "./Arbeidsvaardigheden.jsx";
import Modal from '../../components/common/Modal';
import dateInputToTimestamp from "../../utils/dateInputToTimestamp.js";
import { storedDateToDay, dayToInputValue } from "../../utils/storedDate";
import usePersonaTerms from "../../hooks/usePersonaTerms";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import buildUploadPath from '../../helpers/uploads/buildUploadPath';
import PersonaStatusSelect from '../../components/personas/PersonaStatusSelect';
import { statusDisplaysFrom } from '../../hooks/useStatusDisplay';
import usePermissions from '../../hooks/usePermissions';

const PersonaBuilder = () => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [personaNavigation, setPersonaNavigation] = useContext(PersonaNavigation)
    const [auth] = useContext(Auth)

    // State
    const [showEditPhoto, setShowEditPhoto] = useState(false)
    const [personaDocid, setPersonaDocid] = useState('123')
    const [title, setTitle] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [outflowReason, setOutflowReason] = useState('')
    const [inactiveReason, setInactiveReason] = useState('');
    const [outflowDate, setOutflowDate] = useState(null);
    const [inactiveDate, setInactiveDate] = useState(null);
    const [editOutflowReason, setEditOutflowReason] = useState(false);
    const [editInactiveReason, setEditInactiveReason] = useState(false);
    const [editOutflowDate, setEditOutflowDate] = useState(false);
    const [editInactiveDate, setEditInactiveDate] = useState(false);
    

    // Hooks
    const personaId = Location()[3]
    const statusChange = usePersonaStatusChange({ changedBy: auth?.ID, onSaved: () => setSaved('flex') })
    // Auditlog: leggen vast dat dit dossier geopend is. Zie
    // docs/features/clientdossier-auditlog.md -- de Cloud Audit Logs zien wel
    // dat er gelezen wordt, maar niet WELK dossier.
    useLogRecordOpen('persona', personaId)
    // Meeliften op één useSettings: een eigen useStatusDisplays() zou een
    // tweede stel listeners op hetzelfde document opzetten.
    const { impactFunctionality, personaStatusConfig } = useSettings()
    const statusDisplay = useMemo(
        () => statusDisplaysFrom({ personaStatusConfig }).persona,
        [personaStatusConfig],
    )
    const personaTerms = usePersonaTerms()

    // De knop Opslaan staat in de voet van de gedeelde modal en dus BUITEN
    // de ReadOnlyGuard om de invoervelden. Een uitgeschakelde fieldset dekt
    // hem daar niet meer, dus de poort moet hier apart heen.
    // Bewust NIET `canEdit`: die naam deelt ReadOnlyGuard hieronder al uit
    // als renderparameter, en een tweede zou die overschaduwen.
    // AI-CONTEXT: See docs/pitfalls.md#dev-reused-name-repoints-consumers
    // AI-CONTEXT: See docs/patterns.md#hook-permission-gate
    const { has, ready, worksIn } = usePermissions()
    // Voor de ster in de kop: dezelfde lezing als ReadOnlyGuard, zonder diens
    // fieldset en melding — die horen niet in een paginakop.
    const canEditStar = !ready || has('personas.edit')
    const canEditStatus = !ready || has('personas.edit')

    const personaTabs = useMemo(() => {
        const items = [
            { id: 'dashboard', label: 'Overzicht' },
            { id: 'data', label: 'Gegevens' },
            { id: 'presence', label: 'Rooster' },
            { id: 'report', label: 'Notities' },
        ]
        // Naast de module van de organisatie ook het onderdeel van de rol: een
        // planner die niet met impact meten werkt, heeft aan deze vier niets.
        // Verbergen, geen beveiliging -- zie helpers/roleAreas.js.
        if (impactFunctionality && worksIn('impactMeten')) {
            items.push({ id: 'goals', label: 'Doelen' })
            items.push({ id: 'research', label: 'Onderzoeken' })
            items.push({ id: 'participatieladder', label: 'Participatieladder' })
            items.push({ id: 'arbeidsvaardigheden', label: 'Arbeidsvaardigheden' })
        }
        return items
    }, [impactFunctionality, worksIn])

    // Een link mag een tabblad openen: /personabuilder/<id>?tab=research
    // (de melding over een meetmoment stuurt daarheen). Alleen een tabblad dat
    // er voor deze organisatie en rol ook echt is; de tabbladen van Impact
    // meten verschijnen pas als de instellingen en rechten geladen zijn, dus
    // dit kijkt opnieuw zodra de lijst groeit — en past één keer per link toe,
    // zodat een eigen tabkeuze daarna niet teruggezet wordt.
    const { search, hash } = useLocation()
    // En naar het blok in de hash, bijv. #persona-traject vanaf de pagina Facturatie.
    useScrollToHash(hash)
    const toegepasteTabLink = useRef('')
    useEffect(() => {
        const tab = new URLSearchParams(search).get('tab')
        if (!tab || toegepasteTabLink.current === search) return
        if (!personaTabs.some((t) => t.id === tab)) return
        toegepasteTabLink.current = search
        setPersonaNavigation(tab)
    }, [search, personaTabs, setPersonaNavigation])

    // Firestore
    const personas = useFirestoreID('Personas', personaId ? personaId : '')

    // Set persona meta
    useEffect(() => {
        personas && personas.map(item => (
            setPersonaDocid(item.docid),
            setTitle(item.Name),
            setStartDate(item.StartDate),
            setEndDate(item.EndDate)
        ))
    }, [personas])

    const nameHandler = (e) => {

        const docid = e.target.dataset.docid
        const value = e.target.value

        db.collection('Personas')
        .doc(docid)
        .update({
            Name: value
        })
        .then(() => {
            setSaved('flex')
         })

    }

    const photoHandler = (e) => {

        const photo = e.target.files[0]
        const docid = e.target.dataset.docid

        // Uniek pad: een ruwe bestandsnaam laat twee mensen met dezelfde
        // 'foto.jpg' elkaars afbeelding overschrijven.
        // AI-CONTEXT: See docs/inbox.md #122
        const storageRef = bucket.ref(buildUploadPath(photo.name, "ProfilePhotos"));
        const uploadTask = storageRef.put(photo)

        uploadTask.then(() => {
          
            uploadTask.on('state_changed', snapshot => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            switch (snapshot.state) {
            case firebase.storage.TaskState.PAUSED:
                console.log('Upload is paused');
                break;
            case firebase.storage.TaskState.RUNNING:
                console.log('Upload is running');
                break;
            }
            }, (err) => {
                alert(err)
            }, () => {
            uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {
            console.log('File available at', downloadURL);

            db.collection('Personas')
            .doc(docid)
            .update({
                Photo: downloadURL
            })
            .then(() => {
                setSaved('flex')
                setShowEditPhoto(false)
             })

                })
            })
        }).catch((err) => {
            // Zonder deze afhandeling verdwijnt een geweigerde upload (te groot,
            // of tegengehouden door storage.rules) in een niet-afgevangen
            // belofte: geen melding, en de gebruiker ziet niets gebeuren.
            alert(`Uploaden mislukt: ${err?.message || err}`)
        })
    }  

    // Status Handler — uitstroom en inactiviteit openen het venster, de rest
    // wordt meteen geschreven. Alleen de velden van díe wissel.
    // AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field
    const statusHandler = (e) => {
        const directe = statusChange.request({
            docid: e.target.dataset.docid,
            personaId,
            fromStatus: e.target.dataset.status,
        }, e.target.options[e.target.selectedIndex].value)
        // Een wissel zonder venster heeft geen plek voor de melding.
        if (directe) directe.then((melding) => { if (melding) alert(melding) })
    }

    // Inline edit save handlers
  const outflowReasonSave = docid => {
    db.collection('Personas').doc(docid).update({ OutflowReason: outflowReason })
      .then(() => {
        setSaved('flex');
        setEditOutflowReason(false);
      });
  };

    const inactiveReasonSave = docid => {
    db.collection('Personas').doc(docid).update({ InactiveReason: inactiveReason })
      .then(() => {
        setSaved('flex');
        setEditInactiveReason(false);
      });
  };

  const outflowDateSave = docid => {
    db.collection('Personas').doc(docid).update({ OutflowDate: dateInputToTimestamp(outflowDate) })
      .then(() => {
        setSaved('flex');
        setEditOutflowDate(false);
      });
  };

  const inactiveDateSave = docid => {
    db.collection('Personas').doc(docid).update({ InactiveDate: dateInputToTimestamp(inactiveDate) })
      .then(() => {
        setSaved('flex');
        setEditInactiveDate(false);
      });
  };

  const handleDateChange = (e, type) => {
    const date = e.target.value;
    if (type === 'outflow') setOutflowDate(date);
    else setInactiveDate(date);
  };


    // Render meta fields
  const statusMeta = (status, meta, docid, canEdit) => {
    if (status === 'inactive') {
      return (
        <div>
          <div className="persona-status-meta-item-container">
            {editInactiveReason ? (
              <>
                <p><b>Reden:</b></p>
                <textarea
                  value={inactiveReason}
                  onChange={e => setInactiveReason(e.target.value)}
                  style={{ width: '100%', height: 80 }}
                />
                <button onClick={() => inactiveReasonSave(docid)}>Opslaan</button>
              </>
            ) : (
              <>
                <p><b>Reden:</b> {meta.InactiveReason || 'Geen reden opgegeven'}</p>
                {canEdit && <img src={editIcon} alt="edit" onClick={() => { setInactiveReason(meta.InactiveReason || ''); setEditInactiveReason(true) }} />}
              </>
            )}
          </div>
          <div className="persona-status-meta-item-container">
            {editInactiveDate ? (
              <>
                <p><b>Datum:</b></p>
                <input
                  type="date"
                  value={inactiveDate || ''}
                  onChange={e => handleDateChange(e, 'inactive')}
                />
                <button onClick={() => inactiveDateSave(docid)}>Opslaan</button>
              </>
            ) : (
              <>
                <p><b>Datum:</b> {meta.InactiveDate ? timestampToUI(meta.InactiveDate) : 'Geen datum opgegeven'}</p>
                {canEdit && <img src={editIcon} alt="edit" onClick={() => { setInactiveDate(dayToInputValue(storedDateToDay(meta.InactiveDate))); setEditInactiveDate(true) }} />}
              </>
            )}
          </div>
        </div>
      );
    }

    if (status === 'outflow') {
      return (
        <div>
          <div className="persona-status-meta-item-container">
            {editOutflowReason ? (
              <>
                <p><b>Reden:</b></p>
                <textarea
                  value={outflowReason}
                  onChange={e => setOutflowReason(e.target.value)}
                  style={{ width: '100%', height: 80 }}
                />
                <button onClick={() => outflowReasonSave(docid)}>Opslaan</button>
              </>
            ) : (
              <>
                <p><b>Reden:</b> {meta.OutflowReason || 'Geen reden opgegeven'}</p>
                {canEdit && <img src={editIcon} alt="edit" onClick={() => { setOutflowReason(meta.OutflowReason || ''); setEditOutflowReason(true) }} />}
              </>
            )}
          </div>
          <div className="persona-status-meta-item-container">
            {editOutflowDate ? (
              <>
                <p><b>Datum:</b></p>
                <input
                  type="date"
                  value={outflowDate || ''}
                  onChange={e => handleDateChange(e, 'outflow')}
                />
                <button onClick={() => outflowDateSave(docid)}>Opslaan</button>
              </>
            ) : (
              <>
                <p><b>Datum:</b> {meta.OutflowDate ? timestampToUI(meta.OutflowDate) : 'Geen datum opgegeven'}</p>
                {canEdit && <img src={editIcon} alt="edit" onClick={() => { setOutflowDate(dayToInputValue(storedDateToDay(meta.OutflowDate))); setEditOutflowDate(true) }} />}
              </>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    // Eén ophaalactie voor deze pagina: de Overzicht-kaart en de chips in de
    // Onderzoeken-tab kijken allebei mee.
    <MeasurementSignalsProvider entityId={personaId} entityKind="persona" enabled={Boolean(personaId)}>
    <div className="main">
      <div className="main-container" >
        <div className='page-header'>
            <PersonOutlineOutlinedIcon/>
            <h1>{personaTerms.singular} instellingen</h1>
            {/* De ster 'extra aandacht', zie AdressDetails. */}
            {Array.isArray(personas) && personas[0] && (
                <StarToggle
                    starred={isStarred(personas[0])}
                    disabled={!canEditStar}
                    onToggle={(next) => db.collection('Personas').doc(personas[0].docid).update(starredPatch(next, auth?.ID, timestamp))}
                />
            )}
        </div>
            <div id='live-report-builder-content-container'>
                <ReadOnlyGuard edit="personas.edit">
                    {(canEdit) => personas && personas.map(item => (
                        <div key={item.ID} id='persona-personal-data-container'>
                            <div id='edit-persona-phor-container'>
                                <img className='persona-image' src={item.Photo} alt="" />
                                {canEdit && <img id='edit-persona-photo-icon' src={editIcon} alt="" onClick={() => setShowEditPhoto(!showEditPhoto)}/>}
                            </div>
                            <input type="file" data-docid={item.docid} onChange={photoHandler} style={{display: showEditPhoto ? 'block' : 'none'}}/>
                            <input id='edit-persona-name-input' type="text" defaultValue={item.Name} data-docid={item.docid} onChange={nameHandler}/>
                            <div id='persona-status-container'>
                                <PersonaStatusSelect
                                        value={item.Status}
                                        display={statusDisplay}
                                        docid={item.docid}
                                        onChange={statusHandler}
                                    />
                                 {statusMeta(item.Status, item, item.docid, canEdit)}
                            </div>
                        </div>
                    ))}
                </ReadOnlyGuard>
                <SelectableGrid
                    items={personaTabs}
                    getId={(item) => item.id}
                    getLabel={(item) => item.label}
                    selectedId={personaNavigation}
                    onClick={setPersonaNavigation}
                />
                {personas && personas.map(item => (
                    <div key={item.ID}>
                        <PersonaDashboard personaNavigation={personaNavigation} item={item}/>
                        <Data personaNavigation={personaNavigation} item={item} personaDocid={personaDocid}/>
                        <Shedule personaNavigation={personaNavigation} item={item} courseStartDate={startDate} courseEndDate={endDate}/>
                        <Research personaNavigation={personaNavigation} item={item} personaId={personaId} personaDocid={personaDocid}/>
                        <Reports personaNavigation={personaNavigation} item={item} personaId={personaId} personaDocid={personaDocid}/>
                        <Goals personaNavigation={personaNavigation} item={item}/>
                        <Participatieladder personaNavigation={personaNavigation} item={item}/>
                        <Arbeidsvaardigheden personaNavigation={personaNavigation} item={item}/>
                    </div>
                ))}
            </div>

        </div>
        <Modal
        modalOpen={statusChange.open}
        setModalOpen={(open) => { if (!open) statusChange.cancel() }}
        pageHeader={statusChange.status === 'outflow' ? 'Uitstroom' : 'Inactiviteit'}
        confirm={statusChange.confirm}
        confirmDisabled={!canEditStatus || statusChange.saving}
        content={
            <ReadOnlyGuard edit="personas.edit">
                <h3>Reden {statusChange.status === 'outflow' ? 'uitstroom' : 'inactiviteit'}</h3>
                <p>Geef een reden op voor de {statusChange.status === 'outflow' ? 'uitstroom' : 'inactiviteit'}.</p>
                <textarea
                    placeholder={statusChange.status === 'outflow' ? 'Reden voor uitstroom...' : 'Reden voor inactiviteit...'}
                    value={statusChange.reason}
                    onChange={(e) => statusChange.setReason(e.target.value)}
                ></textarea>
                <h3>Datum van {statusChange.status === 'outflow' ? 'uitstroom' : 'inactiviteit'}</h3>
                <input type="date" value={statusChange.date} onChange={(e) => statusChange.setDate(e.target.value)}/>
                {statusChange.error && <p className="persona-status-error">{statusChange.error}</p>}
            </ReadOnlyGuard>
        }
        />
    </div>
    </MeasurementSignalsProvider>
  )
}

export default PersonaBuilder
